<template>
  <b-modal
    v-model="active"
    class="modal"
    centered
    hide-footer
    no-close-on-backdrop
    no-close-on-esc
  >
    <template #modal-title>
      Передать ТМЦ 
      <span
        v-if="isInitialized"
        class="guid"
      >
        №{{ item.guid }}
      </span>
      <div class="name">
        {{ item.name || '&nbsp;' }}
      </div>
    </template>
    <Loader
      v-if="!isInitialized && busy"
      label="Данные загружаются"
      :height="249"
      :active="true"
    />
    <vee-observer
      v-else
      v-slot="{ handleSubmit, errors }"
      slim
    >
      <form
        class="form"
        @submit.prevent="handleSubmit(submit)"
      >
        <div class="inputs">
          <div class="inputs__row">
            <div class="date-input">
              <vee-provider
                vid="date"
                rules="required|date"
                slim
              >
                <Input
                  ref="calendar"
                  v-model="form.date"
                  label="Дата передачи"
                  placeholder="00.00.0000"
                  format="DD.MM.YYYY, dddd"
                  type="date"
                  icon="calendar"
                  :error="errors.date"
                  :editable="true"
                  :icon-right="true"
                  :required-mark="true"
                  :actually-normal-error-messages="true"
                />
              </vee-provider>
            </div>
            <Input
              v-model="form.workerBy"
              floating-label="Кем передан"
              type="select"
              :items="workers"
              :searchable="true"
              :clearable="true"
            />
          </div>
          <div class="worker-to">
            <vee-provider
              vid="workerTo"
              rules="required"
              slim
            >
              <Input
                v-model="form.workerTo"
                floating-label="Кому передан"
                type="select"
                :items="workers"
                :error="errors.workerTo"
                :searchable="true"
                :clearable="true"
                :required-mark="true"
                :actually-normal-error-messages="true"
              />
            </vee-provider>
          </div>
          <vee-provider
            vid="comment"
            rules="max:500"
            slim
          >
            <InputOld
              v-model="form.comment"
              floating-label="Комментарий"
              type="textarea"
              max-length="500"
              :clearable="true"
            />
          </vee-provider>
          <FileLoader
            ref="fileLoader"
            v-model="form.files"
            actions="loadFiles,preview"
            format="all"
            :limit="10"
          />
        </div>
        <div class="actions">
          <Button
            label="Отменить"
            @click="close"
          />
          <Button
            label="Передать"
            color="primary"
            type="submit"
            :disabled="busy"
          />
        </div>
      </form>
    </vee-observer>
  </b-modal>
</template>

<script>
import moment from "moment";
import serialize from "json-form-data";

import api from "@/plugins/api";

import Loader from "@/components/loader.vue";
import Input from "@/components/controls2/Input.vue";
import InputOld from "@/components/controls/input-old.vue";
import Button from "@/components/controls2/Button.vue";
import FileLoader from "@/components/file-loader.vue";

export default {
  name: "transfer-item-modal",
  components: {
    Loader,
    Input,
    InputOld,
    Button,
    FileLoader,
  },
  props: {
    workerId: {
      type: [String, Number],
      required: true,
    },
  },
  data: () => ({
    isInitialized: false,
    busy: false,
    active: false,
    item: {
      id: null,
      name: null,
      guid: null,
    },
    workers: [],
    form: {
      date: "",
      workerBy: null,
      workerTo: null,
      comment: "",
      files: [],
    },
    workerFrom: null,
  }),
  computed: {
    currentUserId () {
      return Number(this.$store.state.userId);
    },
  },
  methods: {
    async open(itemId) {
      if (!itemId) {
        return;
      }

      this.item = {
        id: null,
        name: null,
        guid: null,
      };

      this.isInitialized = false;
      this.busy = true;

      this.active = true;

      try {
        await this.loadAllWorkers();
        await this.loadFromWorker();

        this.form.date = moment().format("DD.MM.YYYY");
        this.form.workerBy = this.workers.find(worker => worker.value === this.currentUserId);
        this.form.workerTo = null;
        this.form.comment = "";
        this.form.files = [];

        const { data } = await api.get("inventory/view", {
          params: {
            id: itemId,
          },
        });

        this.item = {
          ...this.item,
          ...data,
        };

        this.isInitialized = true;
      } catch (error) {
        console.error(error);
        this.close();
      }

      this.busy = false;

    },
    close() {
      this.active = false;
      this.$emit("close");
      
      if (this.$refs.fileLoader) {
        this.$refs.fileLoader.reset();
      }
    },
    async loadAllWorkers() {
      const { response } = await this.$store.dispatch(
        "personalEmployee/getWorkersList",
        this.currentUserId,
      );

      this.workers = response.map((item) => ({
        value: item.id,
        label: item.name,
        isUser: item.is_user,
      }));
    },
    async loadFromWorker () {
      const { response } = await this.$store.dispatch(
        "personalEmployee/getUserInfo",
        this.workerId,
      );

      this.workerFrom = response;
    },
    async submit() {
      this.busy = true;

      try {
        const { item } = this;
        const { workerBy } = this.form;

        const params = {
          action: 4,
          worker_id: this.workerId,
          inventory_id: item.id,
          recipient_worker_id: this.form.workerTo.value,
          date: this.form.date,
          comment: this.form.comment,
          files: this.form.files,
        };

        if (workerBy) {
          if (workerBy.isUser) {
            params.author_user_id = workerBy.value;
          } else {
            params.author_worker_id = workerBy.value;
          }
        }

        const formData = serialize(params);
        
        const { data } = await api.post("inventory-history/create", formData);

        this.$emit("submit", data);

        this.$notify({
          group: "success-message",
          data: {
            textMessage: `Позиция <b>№${item.guid} ${item.name}</b> успешно передана от сотрудника <b>${this.workerFrom.name}</b> сотруднику <b>${this.form.workerTo.label}</b>`,
          },
        });

        this.close();
      } catch (error) {
        console.error(error);

        this.$notify({
          group: "error-message",
          data: {
            textMessage: "Произошла ошибка. Попробуйте ещё раз",
          },
        });

        this.busy = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import '~@/assets/styles/vars';

::v-deep .modal .modal-dialog {
  max-width: 590px;

  @include onlyMobile {
    margin: 0;
  }
}

.form {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.guid {
  font-weight: 400;
}

.name {
  margin-top: 5px;
  font-size: 13px;
  font-weight: 400;
  line-height: 16px;
}

.inputs {
  display: flex;
  flex-direction: column;
  margin-bottom: 25px;
  gap: 25px;

  &__row {
    display: flex;
    align-items: baseline;
    gap: inherit;

    > div {
      flex-grow: 1;
    }
  }

  @include onlyMobile {
    &__row {
      flex-direction: column;
      align-items: stretch;
    }
  }
}

.actions {
  display: flex;
  justify-content: flex-end;
  margin-top: auto;
  gap: 10px;

  @include onlyMobile {
    justify-content: stretch;
    margin: auto -20px -20px -20px;
    gap: 0;
    
    > button {
      flex-grow: 1;
      border-radius: 0;
      border-left: none;
      border-right: none;
      border-bottom: none;
    }
  }
}

.date-input {
  max-width: 125px;
}

.worker-to {
  max-width: 372px;

  @include onlyMobile {
    max-width: 100%;
  }
}

</style>
